import { updateProductsPerInsuranceTarget } from '@redux/actions/recommended-products.actions';
import { generateProductFromOfferingProduct, OfferingProduct } from './coinscrap/offering-product.model';
import { Config } from './config.model';
import { InsuranceTarget } from './insurance-target.model';
import { LifePlan } from './life-plan.model';
import { deepCloneProduct, Product, updateProductWithProductConfig } from './product.model';
import { getProductAvailableTemporalities } from './temporality.model';
import { updateLifePlanInsuranceTargetCurrentProduct } from '@redux/actions/life-plan.actions';
import { LifePlanInsurances } from './insurance.model';

export interface ProductsRecommendationPerTarget {
  targetId: string;
  recommended?: Product;
  available: Product[];
}

export interface ProductsPerInsuranceTarget {
  [key: string]: ProductsRecommendationPerTarget;
}

export function deepCloneProductsPerInsuranceTarget(
  productsPerInsuranceTarget: ProductsPerInsuranceTarget
): ProductsPerInsuranceTarget {
  // TODO: use a better approach
  return JSON.parse(JSON.stringify(productsPerInsuranceTarget));
}

export function setRecommendedProducts(
  lifePlan: LifePlan,
  recommendedLifePlanProducts: ProductsPerInsuranceTarget,
  scope?: 'all' | string
): LifePlan {
  const lifePlanInsurances = lifePlan.insurances;
  if (lifePlanInsurances) {
    Object.entries(lifePlanInsurances).forEach(([id, insuranceTarget]) => {
      const recommendations = recommendedLifePlanProducts[id];
      if (!recommendations && lifePlan.type !== 'COMPLETE') {
        // Unique lifeplan insurance target should not present insurance targets
        // that are not valid on recomendations
        delete lifePlanInsurances[id];
        return;
      }
      const recommendedProduct = recommendedLifePlanProducts[id]?.recommended;
      if (recommendedProduct) {
        if (
          scope === 'all' ||
          !insuranceTarget.currentProduct ||
          (scope === insuranceTarget.id && !insuranceTarget.currentProduct.cartStatus.added)
        ) {
          insuranceTarget.currentProduct = recommendedProduct;
        }
        insuranceTarget.currentProduct = updateProductMilestone(insuranceTarget.currentProduct, insuranceTarget);
      } else {
        // Clean the insurance target current product, this means no one applies
        insuranceTarget.currentProduct = undefined;
      }
    });
  }
  lifePlan.insurances = lifePlanInsurances;
  return lifePlan;
}

export function generateProductsPerInsuranceTargetWithConfigData(
  productsPerInsuranceTarget: ProductsPerInsuranceTarget,
  config: Config
) {
  const products = deepCloneProductsPerInsuranceTarget(productsPerInsuranceTarget);
  Object.entries(products).forEach(([id, productsRecommendations]) => {
    const recommendedProduct = productsRecommendations.recommended;
    if (recommendedProduct) {
      const recommendedProductConfig = config.products[recommendedProduct.id];
      if (recommendedProductConfig) {
        updateProductWithProductConfig(recommendedProduct, recommendedProductConfig);
      }
    }
    productsRecommendations.available?.forEach(product => {
      const configProduct = config.products[product.id];
      if (configProduct) {
        updateProductWithProductConfig(product, configProduct);
      }
    });
  });
  return products;
}

export function generateProductsRecommendationsPerTargetFromOfferingProduct(
  targetId: string,
  associatedOfferingProducts: OfferingProduct[],
  config: Config,
  lifePlan: LifePlan
): ProductsRecommendationPerTarget {
  let recommendedProduct: Product | undefined;
  const availableProducts: Product[] = [];
  associatedOfferingProducts.forEach(p => {
    const product = generateProductFromOfferingProduct(p, config);
    availableProducts.push(product);
    if (p.recommendedConfiguration) {
      recommendedProduct = product;
    }
  });
  //recommendedProduct = undefined;
  if (!recommendedProduct && availableProducts.length > 0) {
    recommendedProduct = availableProducts[0];
  }
  return {
    targetId,
    available: availableProducts,
    recommended: recommendedProduct
  };
}

export function updateProductMilestone(product: Product, insuranceTarget: InsuranceTarget): Product {
  const temporalities = getProductAvailableTemporalities(
    product,
    product.selectedCurrency,
    insuranceTarget.insured.age
  );
  const temporality = temporalities.find(t => t.id === product.selectedTemporality) ?? temporalities[0];
  switch (temporality.type) {
    case 'until':
      switch (product.insurance) {
        case 'EDUCATION':
          product = Object.assign({}, product, {
            milestone: {
              userAge: insuranceTarget.policyHolder.age + temporality.value - insuranceTarget.insured.age,
              childAge: temporality.value,
              year: new Date().getFullYear() + temporality.value - insuranceTarget.insured.age
            }
          });
          break;
        case 'DEATH':
        case 'SAVING':
        case 'RETIREMENT':
          product = Object.assign({}, product, {
            milestone: {
              userAge: temporality.value,
              year: new Date().getFullYear() + temporality.value - insuranceTarget.policyHolder.age
            }
          });
          break;
      }
      break;
    case 'duration':
      switch (product.insurance) {
        case 'EDUCATION':
          product = Object.assign({}, product, {
            milestone: {
              userAge: insuranceTarget.policyHolder.age + temporality.value,
              childAge: insuranceTarget.insured.age + temporality.value,
              year: new Date().getFullYear() + temporality.value
            }
          });
          break;
        case 'DEATH':
        case 'SAVING':
        case 'RETIREMENT':
          product = Object.assign({}, product, {
            milestone: {
              userAge: insuranceTarget.policyHolder.age + temporality.value,
              year: new Date().getFullYear() + temporality.value
            }
          });
          break;
      }
      break;
  }
  return product;
}
